import {
  collection,setDoc, addDoc, updateDoc, deleteDoc, orderBy, getDoc,doc, getDocs, query, limit, where, startAfter, writeBatch, Timestamp, onSnapshot
} from "firebase/firestore";
import axios from 'axios';
import { db } from '../Components/auth/firebaseConfig'; // Adjust this path as necessary
import { getCookie } from '../utils/cookieUtils';
import { isSuperAdminEmail } from '../utils/superAdminUtils';
import { deductTenantCredits } from '../utils/firestoreCards';
import { addTenantUser } from '../utils/tenantPricingModule';

// Fetch user groups for a specific tenantId
export const fetchUserGroupsByTenant = async (tenantId) => {
  try {
    const q = query(collection(db, "userGroups"), where("tenantId", "==", tenantId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching user groups by tenant:", error);
    throw error;
  }
};


const PAGE_SIZE = 10; // You can adjust this value as needed

// Helper function to log database operations
const logDBOperation = (operation, collection, error = null) => {
  console.log(`DB Operation: ${operation} on collection ${collection}`, error ? `Error: ${error.message}` : 'Success');
};

export const addProject = async (newProject, tenantId, userId, userName, selectedTemplate) => {
  try {
    // console.log('Starting addProject operation with:', { tenantId, userId, userName, selectedTemplate }); // Commented out to avoid revealing user data
    
    // Check if project with same name exists
    const projectName = newProject.prjName.trim();
    const projectsRef = collection(db, "projects");
    const q = query(projectsRef, where("prjName", "==", projectName), where("tenantId", "==", tenantId));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      throw new Error("Project name is already taken. Cannot create project with same name.");
    }
    
    let columns = [];
    if (selectedTemplate) {
      const templateRef = doc(db, "templates", selectedTemplate);
      const templateSnapshot = await getDoc(templateRef);
      if (templateSnapshot.exists()) {
        const templateData = templateSnapshot.data();
        columns = templateData.columns || [];
        console.log('Template columns fetched:', columns);
      } else {
        console.warn('Template not found:', selectedTemplate);
      }
    }

    const projectData = {
      ...newProject,
      tenantId,
      prjOwnerId: userId,
      prjOwnerName: userName,
      columns,
      lastSerialNumber: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const docRef = doc(collection(db, "projects"));
    await deductTenantCredits(tenantId, 'createProject', 1, {
      userId,
      referenceId: docRef.id,
    });

    console.log('Creating project with data:', { ...projectData, columns });
    await setDoc(docRef, { ...projectData, prjId: docRef.id });
    console.log('Project created with ID:', docRef.id);
    logDBOperation('addProject', 'projects');

    return { id: docRef.id, ...projectData, prjId: docRef.id };
  } catch (error) {
    logDBOperation('addProject', 'projects', error);
    console.error("Error adding project:", error);
    throw error;
  }
};

export const editProject = async (projectId, updatedFields, tenantId) => {
  try {
    const projectRef = doc(db, "projects", projectId);

    // Ensure tenantId is included and not updated
    const projectData = await getDoc(projectRef);
    const existingData = projectData.data();

    await updateDoc(projectRef, { ...updatedFields, tenantId: existingData.tenantId });

    return { id: projectId, ...updatedFields, tenantId: existingData.tenantId };
  } catch (error) {
    console.error("Error updating project:", error);
    throw error;
  }
};

// // Delete a project with cascade delete
// export const deleteProject = async (projectId, tenantId) => {
//   const batch = writeBatch(db);

//   try {
//     // Delete all boards and cards associated with the project
//     const boardsQuery = query(collection(db, "boards"), where("projectId", "==", projectId), where("tenantId", "==", tenantId));
//     const boardsSnapshot = await getDocs(boardsQuery);

//     for (const boardDoc of boardsSnapshot.docs) {
//       const boardId = boardDoc.id;
//       // Delete all cards associated with the board
//       const cardsQuery = query(collection(db, "cards"), where("boardId", "==", boardId));
//       const cardsSnapshot = await getDocs(cardsQuery);

//       for (const cardDoc of cardsSnapshot.docs) {
//         batch.delete(doc(db, "cards", cardDoc.id));
//       }
//       batch.delete(doc(db, "boards", boardId));
//     }

//     // Delete the project
//     batch.delete(doc(db, "projects", projectId));

//     await batch.commit();
//   } catch (error) {
//     console.error("Error deleting project:", error);
//     throw error;
//   }
// };
// Delete a project with cascade delete (deletes boards and associated cards)
export const deleteProject = async (projectId, tenantId) => {
  const batch = writeBatch(db);

  try {
    // Step 1: Query for all boards related to the given projectId and tenantId
    const boardsQuery = query(
      collection(db, "boards"),
      where("projectId", "==", projectId),
      where("tenantId", "==", tenantId)
    );
    const boardsSnapshot = await getDocs(boardsQuery);

    for (const boardDoc of boardsSnapshot.docs) {
      const boardId = boardDoc.id;
      
      // Step 2: Query for all cards related to the current board
      const cardsQuery = query(
        collection(db, "cards"),
        where("boardId", "==", boardId)
      );
      const cardsSnapshot = await getDocs(cardsQuery);

      // Step 3: Delete all cards related to this board
      for (const cardDoc of cardsSnapshot.docs) {
        batch.delete(doc(db, "cards", cardDoc.id));
      }

      // Step 4: Delete the board
      batch.delete(doc(db, "boards", boardId));
    }

    // Step 5: Delete the project
    batch.delete(doc(db, "projects", projectId));

    // Commit the batch to perform all deletions atomically
    await batch.commit();

    console.log("Project and all related boards and cards deleted successfully");
  } catch (error) {
    console.error("Error deleting project:", error);
    throw error;
  }
};
// Fetch projects with pagination
export const fetchProjects = async (lastVisible = null, tenantId) => {
  try {
    let q = query(collection(db, "projects"), where("tenantId", "==", tenantId), limit(PAGE_SIZE));

    if (lastVisible) {
      q = query(collection(db, "projects"), where("tenantId", "==", tenantId), startAfter(lastVisible), limit(PAGE_SIZE));
    }

    const querySnapshot = await getDocs(q);
    const projects = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    const lastDoc = querySnapshot.docs.length
      ? querySnapshot.docs[querySnapshot.docs.length - 1]
      : null;

    return { projects, lastVisible: lastDoc };
  } catch (error) {
    console.error("Error fetching projects:", error);
    throw error;
  }
};


//boards page apis

// import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, where, query } from 'firebase/firestore';
// import { db } from '../Components/auth/firebase';

// CREATE BOARD
// export const createBoard = async (boardData, tenantId,userId) => {
//   try {
//     const docRef = await addDoc(collection(db, 'boards'), {});

//     const boardWithTenantAndId = { ...boardData, tenantId,  boardId: docRef.id,  boardCreatedByUserId: userId ,  boardActiveStatus: true};
//     await setDoc(docRef, boardWithTenantAndId);
//     return { id: docRef.id, ...boardWithTenantAndId };
//   } catch (error) {
//     console.error('Error creating board: ', error);
//     throw error;
//   }
// };
export const createBoard = async (boardData, tenantId, userId) => {
  try {
    // console.log('Starting createBoard operation with:', { tenantId, userId }); // Commented out to avoid revealing user data
    
    const docRef = doc(collection(db, 'boards'));
    console.log('Reserved board ID:', docRef.id);

    // If columns match riskColumns, set templateType
    const riskColumns = [
      'Risk Identification',
      'Analysis',
      'Mitigation Planning',
      'In-Progress',
      'Monitoring',
      'Done',
      'Blocked',
      'Withdrawn'
    ];
    // Trim columns for robust matching
    const boardCols = Array.isArray(boardData.columns) ? boardData.columns.map(col => col.trim()) : [];
    let templateType = boardData.templateType;
    if (!templateType && boardCols.length === riskColumns.length && boardCols.every((col, idx) => col === riskColumns[idx])) {
      templateType = 'Risk Management';
    }
    if (typeof templateType !== 'string') templateType = '';
    const boardWithColumns = { 
      ...boardData, 
      columns: boardData.columns, 
      tenantId, 
      boardId: docRef.id, 
      boardCreatedByUserId: userId, 
      boardActiveStatus: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      templateType
    };

    const creditAction = boardData.boardType === 'Sprint' ? 'createSprintBoard' : 'createBoard';
    await deductTenantCredits(tenantId, creditAction, 1, {
      userId,
      referenceId: docRef.id,
    });
    console.log('Creating board with data:', boardWithColumns);
    await setDoc(docRef, boardWithColumns);
    logDBOperation('createBoard', 'boards');

    return { id: docRef.id, ...boardWithColumns };
  } catch (error) {
    logDBOperation('createBoard', 'boards', error);
    console.error('Error creating board:', error);
    throw error;
  }
};

// READ BOARDS
export const fetchBoards = async (projectId, assignedGroups, tenantId) => {
  try {
    // Validate and clean assignedGroups to avoid Firebase error
    if (!assignedGroups || !Array.isArray(assignedGroups) || assignedGroups.length === 0) {
      console.warn('No assigned groups found for user, returning empty boards array');
      return [];
    }

    // Filter out null, undefined, and empty string values
    const validAssignedGroups = assignedGroups.filter(group => 
      group !== null && group !== undefined && group !== ''
    );

    if (validAssignedGroups.length === 0) {
      console.warn('No valid assigned groups found for user, returning empty boards array');
      return [];
    }

    console.log('Fetching boards with assigned groups:', validAssignedGroups);

    const q = query(
      collection(db, 'boards'),
      where('projectId', '==', projectId),
      where('tenantId', '==', tenantId),
      where('assignedGroups', 'array-contains-any', validAssignedGroups),
      where('boardActiveStatus', '==', true)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching boards: ', error);
    throw error;
  }
};

export const saveBoard = async ({
  projectId,
  newBoard,
  // selectedTemplate,
  prjName,
  boardToEdit,
  boardActiveStatus,
  projectDueDate,
  userGroups,
  tenantId,
  userId,
}) => {
  if (!projectId) throw new Error("Project ID is required");

  // let columns = [];
  // if (selectedTemplate) {
  //   const templateRef = doc(db, "templates", selectedTemplate);
  //   const templateSnapshot = await getDoc(templateRef);
  //   if (templateSnapshot.exists()) {
  //     const templateData = templateSnapshot.data();
  //     columns = templateData.columns;
  //   }
  // }

  // Always fetch columns from the project
  const projectRef = doc(db, "projects", projectId);
  const projectSnap = await getDoc(projectRef);
  let columns = [];
  if (projectSnap.exists()) {
    columns = projectSnap.data().columns || [];
  }

  const formattedDate = new Date().toISOString(); // Current date in ISO format

  const boardData = {
    ...newBoard,
    projectId,
    columns,
    prjName,
    boardcreationDate: boardToEdit ? boardToEdit.boardcreationDate : formattedDate,
    boardActiveStatus,
    boardDueDate: newBoard.boardDueDate || projectDueDate,
    assignedGroups: newBoard.assignedGroups.map((groupId) => {
      const group = userGroups.find((group) => group.id === groupId);
      return group?.id || groupId;
    }),
  };

  if (boardToEdit) {
    await updateBoard(boardToEdit.id, boardData, tenantId, userId, boardActiveStatus);
    return { ...boardData, id: boardToEdit.id };
  } else {
    return await createBoard(boardData, tenantId, userId);
  }
};

const formatDate = (date) => {
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZone: 'Asia/Kolkata', // UTC+5:30 for India Standard Time
    timeZoneName: 'short'
  });
};
// UPDATE BOARD
export const updateBoard = async (boardId, updatedData, tenantId,userId,boardActiveStatus) => {
  try {
    // Get the current Unix timestamp for the update
    const now = new Date();
    const formattedDate = formatDate(now); // Format the date as needed
   
    const boardRef = doc(db, 'boards', boardId);
    await updateDoc(boardRef, { ...updatedData,  boardUpdatedByUserId: userId,  boardUpdatedOn:formattedDate, tenantId ,  boardActiveStatus: boardActiveStatus});
  } catch (error) {
    console.error('Error updating board: ', error);
    throw error;
  }
};

// DELETE BOARD
// export const deleteBoard = async (boardId, tenantId) => {
//   try {
//     const boardRef = doc(db, 'boards', boardId);
//     const boardDoc = await getDoc(boardRef);
    
//     if (boardDoc.exists() && boardDoc.data().tenantId === tenantId) {
//       await deleteDoc(boardRef);
//     } else {
//       throw new Error('Unauthorized or board not found');
//     }
//   } catch (error) {
//     console.error('Error deleting board: ', error);
//     throw error;
//   }
// };
export const deleteBoard = async (boardId, tenantId) => {
  try {
    const boardRef = doc(db, 'boards', boardId);
    const boardDoc = await getDoc(boardRef);

    if (boardDoc.exists() && boardDoc.data().tenantId === tenantId) {
      // Query all cards related to this boardId
      const cardsCollection = collection(db, 'cards');
      const cardsQuery = query(cardsCollection, where('boardId', '==', boardId));
      const cardsSnapshot = await getDocs(cardsQuery);

      // Delete all related cards
      const deleteCardPromises = cardsSnapshot.docs.map((cardDoc) => deleteDoc(cardDoc.ref));
      await Promise.all(deleteCardPromises);

      // Delete the board document
      await deleteDoc(boardRef);

      console.log('Board and related cards deleted successfully!');
    } else {
      throw new Error('Unauthorized or board not found');
    }
  } catch (error) {
    console.error('Error deleting board and related cards: ', error);
    throw error;
  }
};

// src/api/DbQuery.js
// import { db } from '../Components/auth/firebase.js';
// import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';

export const fetchBoardDetails = async (boardId) => {
  try {
    const boardDoc = await getDoc(doc(db, 'boards', boardId));
    if (boardDoc.exists()) {
      return boardDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error fetching board details:', error);
    return null;
  }
};

export const fetchProjectTitle = async (projectId) => {
  try {
    const projectDoc = await getDoc(doc(db, 'projects', projectId));
    if (projectDoc.exists()) {
      return projectDoc.data().title;
    }
    return null;
  } catch (error) {
    console.error('Error fetching project title:', error);
    return null;
  }
};

export const fetchColumnsAndTasks = async (boardId, tenantId) => {
  try {
    // const columnsSnapshot = await getDocs(collection(db, 'columns'));
    // const columnsData = columnsSnapshot.docs.map(doc => doc.data().name);
    const boardRef = doc(db, 'boards', boardId);
    const boardSnapshot = await getDoc(boardRef);

    if (!boardSnapshot.exists()) {
      throw new Error(`Board with ID ${boardId} not found`);
    }

    const boardData = boardSnapshot.data();
    const columnsData = boardData.columns || []; 
    const tasksQuery = query(collection(db, 'tasks'), where('boardId', '==', boardId), where('tenantId', '==', tenantId));
    const tasksSnapshot = await getDocs(tasksQuery);
    
    const tasksData = tasksSnapshot.docs.reduce((acc, doc) => {
      const data = doc.data();
      const columnIndex = columnsData.indexOf(data.cardStatus);
      if (columnIndex !== -1) {
        acc[columnIndex] = acc[columnIndex] ? [...acc[columnIndex], { ...data, id: doc.id }] : [{ ...data, id: doc.id }];
      }
      return acc;
    }, {});

    return { columnsData, tasksData };
  } catch (error) {
    console.error('Error fetching columns and tasks:', error);
    return { columnsData: [], tasksData: {} };
  }
};

export const fetchTasksByBoard = async (projectId, selectedBoardId, boardType, tenantId) => {
  try {
    let tasksQuery;

    if (selectedBoardId === 'ALL_BOARDS') {
      tasksQuery = query(collection(db, 'cards'), where('projectId', '==', projectId), where('tenantId', '==', tenantId));
    } else if (boardType === 'Sprint') {
      // Fetch all cards for the project, filter by assignedSprints containing this sprint
      tasksQuery = query(collection(db, 'cards'), where('projectId', '==', projectId), where('tenantId', '==', tenantId));
    } else {
      tasksQuery = query(collection(db, 'cards'), where('boardId', '==', selectedBoardId), where('tenantId', '==', tenantId));
    }

    const tasksSnapshot = await getDocs(tasksQuery);
    let cards = tasksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log("All cards fetched for project:", projectId, "tenant:", tenantId, cards);
    console.log("Sprint filter: selectedBoardId", selectedBoardId, "assignedSprints in cards", cards.map(c => c.assignedSprints));
    // For Sprint board, filter cards where assignedSprints contains this sprint
    if (boardType === 'Sprint') {
      console.log("Filtering for Sprint board:", selectedBoardId, cards.map(c => c.assignedSprints));
      cards = cards.filter(card =>
        Array.isArray(card.assignedSprints) &&
        card.assignedSprints.some(s => s.boardId === selectedBoardId)
      );
      console.log("Cards after Sprint filter:", cards);
    }
    return cards;
  } catch (error) {
    console.error("Error fetching tasks:", error);
    throw error;
  }

};

export const addTask = async (task, boardId, projectId, tenantId) => {
  let docRef = null;
  try {
    const newTask = { ...task, boardId, projectId, tenantId };
    docRef = doc(collection(db, 'cards'));
    await deductTenantCredits(tenantId, 'addCard', 1, {
      userId: task.cardCreatedByUserEmail || task.createdByUserEmail || '',
      referenceId: docRef.id,
    });
    await setDoc(docRef, newTask);
    return { ...newTask, id: docRef.id };
  } catch (error) {
    console.error('Error adding task:', error);
    return null;
  }
};

export const editTask = async (taskId, updatedTask) => {
  try {
    const taskRef = doc(db, 'cards', taskId);
    await updateDoc(taskRef, updatedTask);
    return true;
  } catch (error) {
    console.error('Error updating task:', error);
    return false;
  }
};

export const deleteTask = async (taskId) => {
  try {
    await deleteDoc(doc(db, 'cards', taskId));
    return true;
  } catch (error) {
    console.error('Error deleting task:', error);
    return false;
  }
};

export const loginWithUserManagementApi = async (loginData) => {
  const response = await axios.post(
    'https://user-management-api-v1.onrender.com/api/auth/login',
    loginData
  );

  if (response.status !== 200) {
    throw new Error(response.data?.message || 'Login failed');
  }

  return response.data;
};

export const fetchTenantBranding = async (tenantId) => {
  if (!tenantId) {
    return {
      companyLogoUrl: '',
      organizationName: ''
    };
  }

  const tenantQ = query(collection(db, 'tenant'), where('tenantId', '==', tenantId));
  const tenantSnap = await getDocs(tenantQ);

  if (tenantSnap.empty) {
    return {
      companyLogoUrl: '',
      organizationName: tenantId || 'Organization'
    };
  }

  const tenant = tenantSnap.docs[0].data() || {};
  return {
    companyLogoUrl: tenant.companyLogoUrl || '',
    organizationName: tenant.companyName || tenant.tenantName || tenantId || 'Organization'
  };
};

export const ensureSuperAdminUserRecord = async (user) => {
  const normalizedEmail = user?.email?.toLowerCase();
  if (!normalizedEmail) {
    throw new Error('User email is required to ensure superadmin record');
  }

  const usersQuery = query(
    collection(db, 'users'),
    where('email', '==', normalizedEmail)
  );
  const userSnapshot = await getDocs(usersQuery);

  if (!userSnapshot.empty) {
    return userSnapshot.docs[0].data();
  }

  const newUserRef = await addDoc(collection(db, 'users'), {
    email: normalizedEmail,
    userName: user.displayName || normalizedEmail.split('@')[0],
    role: 'admin',
    userId: '',
    tenantId: '',
    assignedGroups: []
  });

  await updateDoc(doc(db, 'users', newUserRef.id), {
    userId: newUserRef.id
  });

  return {
    email: normalizedEmail,
    userName: user.displayName || normalizedEmail.split('@')[0],
    role: 'admin',
    userId: newUserRef.id,
    tenantId: '',
    assignedGroups: []
  };
};

export const createTemplate = async (templateData, tenantId, userId, userEmail, makeGlobal = false) => {
  try {
    // Add a document to the "templates" collection
    const docRef = await addDoc(collection(db, 'templates'), {});

    // Prepare the template data with metadata
    const templateWithMeta = {
      ...templateData,
      tenantId,
      templateId: docRef.id,
      createdByUserId: userId,
      activeStatus: true,
      global: makeGlobal && isSuperAdminEmail(userEmail),
    };

    // Write the complete data to the document
    await setDoc(docRef, templateWithMeta);

    return { id: docRef.id, ...templateWithMeta };
  } catch (error) {
    console.error('Error creating template:', error);
    throw error;
  }
};

// function to fetch user details
export const fetchUserDetails = async (token) => {
  const isProd = process.env.REACT_APP_STAGE === "PROD";
  const userData = getCookie('userData');
  // console.log('Retrieved userData:', userData); // Commented out to avoid revealing user data
  // console.log('userData.email:', userData?.email); // Commented out to avoid revealing user data

  if(!token || !userData?.email){
    throw new Error("Token or Email is required but not found");
  }

  if(isProd || !process.env.REACT_APP_STAGE){
    try{
      const response = await fetch('https://user-management-api-latest.onrender.com/api/protected/user-details', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to fetch user details:', response.status);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  }
  else {
    try {
      console.log("Bypassing API");

      // In DEV, tenant selection is stored in cookie during login.
      // Prefer cookie values so we do not override with the first email match.
      if (userData?.tenantId && userData?.role) {
        return {
          role: userData.role,
          tenantId: userData.tenantId,
          email: userData.email,
          userId: userData.userId,
          userName: userData.userName
        };
      }

      const userDetails = await fetchUserIdByEmail(userData.email, userData?.tenantId);
      // console.log("Fetched user details from Firestore:", userDetails); // Commented out to avoid revealing user data

      if(userDetails){
        return {
          role: userDetails.role,
          tenantId: userDetails.tenantId,
          email: userDetails.email,
          userId: userDetails.userId,
          userName: userDetails.userName
        };
      } else {
        throw new Error(`User with email ${userData.email} not found in Firestore`);
      }
    } catch (error) {
      console.error('Error fetching user details from Firestore:', error);
      throw error;
    }
  }
};

export const fetchAssignedGroupsByEmail = async (email) => {
  try {
      const userQuery = query(collection(db, 'users'), where('email', '==', email));
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
          const userData = userSnapshot.docs[0].data();
          return {
              userId: userData.userId,
              assignedGroups: userData.assignedGroups || []
          };
      } else {
          console.warn('User with specified email not found');
          return { userId: null, assignedGroups: [] };
      }
  } catch (error) {
      console.error('Error fetching assigned groups:', error);
      return { userId: null, assignedGroups: [] };
  }
};

export const fetchProjectDetails = async (projectId) => {
  if (!projectId) return null;

  try {
      const docRef = doc(db, 'projects', projectId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
          return docSnap.data(); // Returns { prjName, dueDate, startDate, ... }
      } else {
          console.warn('Project not found');
          return null;
      }
  } catch (error) {
      console.error('Error fetching project details:', error);
      return null;
  }
};


export const fetchUserGroups = async () => {
  try {
    const q = collection(db, "userGroups");
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching user groups:", error);
    throw error;
  }
};

export const fetchTemplates = async (tenantId) => {
  try {
    const templatesCollection = collection(db, "templates");
    const tenantQ = query(templatesCollection, where("tenantId", "==", tenantId));
    const globalQ = query(templatesCollection, where("global", "==", true));
    const tenantSnap = await getDocs(tenantQ);
    const globalSnap = await getDocs(globalQ);

    // Merge and deduplicate by id
    const allDocs = [...tenantSnap.docs, ...globalSnap.docs];
    const uniqueTemplates = Array.from(
      new Map(allDocs.map(doc => [doc.id, {
        id: doc.id,
        templateName: doc.data().templateName,
      }])).values()
    );
    return uniqueTemplates;
    } catch (error) {
    console.error("Error fetching templates:", error);
    throw error;
  }
};

export const fetchBoardColumns = async (boardId) => {
  if (!boardId) return [];

  try {
    const boardDocRef = doc(db, "boards", boardId);
    const boardDoc = await getDoc(boardDocRef);

    if (boardDoc.exists()) {
      const boardData = boardDoc.data();
      const columnsArray = boardData.columns || [];

      // Transform the columns array into the desired format
      return columnsArray.map((column, index) => ({
        id: `column-${index + 1}`,
        name: column,
      }));
    } else {
      
      return [];
    }
  } catch (error) {
   
    throw error;
  }
};

export const fetchCardsForBoard = async (boardId, tenantId) => {
  try {
    const cardsCollection = collection(db, "cards");
    const querySnapshot = await getDocs(
      query(cardsCollection, where("boardId", "==", boardId), where("tenantId", "==", tenantId))
    );
    console.log(querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    } 
  )));

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    } 
  )
  );
  } catch (error) {
    
    return [];
  }
};

export const fetchAnalyticsData = async (selectedBoardId, projectId) => {
  try {
    let cardsQuery;

    if (selectedBoardId === "ALL_BOARDS") {
      cardsQuery = query(collection(db, "cards"), where("projectId", "==", projectId));
    } else {
      cardsQuery = query(collection(db, "cards"), where("boardId", "==", selectedBoardId));
    }

    const cardsSnapshot = await getDocs(cardsQuery);
    const cards = cardsSnapshot.docs.map((doc) => doc.data());

    if (cards.length === 0) {
      return { totalCardsInBoard: 0, cardsPerColumnInBoard: {}, addedProjects: 0, updatedProjects: 0 };
    }

    const totalCardsInBoard = cards.length;
    const cardsPerColumnInBoard = cards.reduce((acc, card) => {
      const columnName = card.cardStatus;
      acc[columnName] = (acc[columnName] || 0) + 1;
      return acc;
    }, {});

    const twoDaysAgo = Timestamp.fromDate(new Date(Date.now() - 48 * 60 * 60 * 1000));

    const addedProjectsQuery = query(collection(db, "projects"), where("cardCreationDate", ">=", twoDaysAgo));
    const addedProjectsSnapshot = await getDocs(addedProjectsQuery);
    const addedProjects = addedProjectsSnapshot.docs.length;

    const updatedProjectsQuery = query(collection(db, "projects"), where("updatedAt", ">=", twoDaysAgo));
    const updatedProjectsSnapshot = await getDocs(updatedProjectsQuery);
    const updatedProjects = updatedProjectsSnapshot.docs.length;

    return {
      totalCardsInBoard,
      cardsPerColumnInBoard,
      addedProjects,
      updatedProjects,
    };
  } catch (error) {
    throw new Error("Error fetching analytics data: " + error.message);
  }
};

export const fetchRecentCardsFromDB = async (selectedBoardId, projectId, twoDaysAgo, convertStringToDate) => {
  try {
    if (!selectedBoardId) return 0; // Ensure a valid board ID is provided

    let recentCardsQuery;

    if (selectedBoardId === "ALL_BOARDS") {
      recentCardsQuery = query(collection(db, "cards"), where("projectId", "==", projectId));
    } else {
      recentCardsQuery = query(collection(db, "cards"), where("boardId", "==", selectedBoardId));
    }

    const recentCardsSnapshot = await getDocs(recentCardsQuery);

    // Filter cards based on creation date
    const recentCards = recentCardsSnapshot.docs.filter((doc) => {
      const cardData = doc.data();
      const cardDate = convertStringToDate(cardData.cardCreationDate); // Convert string to Date
      return cardDate >= twoDaysAgo; // Compare dates
    });

    return recentCards.length; // Return the count of recent cards
  } catch (error) {
    throw new Error("Error fetching recent cards: " + error.message);
  }
};

export const updateUserGroupRole = async (id, newRole) => {
  try {
    const userGroupRef = doc(db, "userGroups", id);
    await updateDoc(userGroupRef, { accessPermission: newRole });
    return { id, accessPermission: newRole };
  } catch (error) {
    
    throw error;
  }
};

export const deleteUserGroup = async (id) => {
  try {
    await deleteDoc(doc(db, "userGroups", id));
    return id; // Return ID of the deleted group
  } catch (error) {
   
    throw error;
  }
};

// Function to fetch report data with date conversion
export const fetchReportData = async (reportType, startDate, endDate) => {
  try {
    let q;

    if (reportType === 'projects') {
      q = query(
        collection(db, 'projects'),
        where('startDate', '>=', startDate),
        where('dueDate', '<=', endDate)
      );
    } else if (reportType === 'boards') {
      q = query(
        collection(db, 'boards'),
        where('boardcreationDate', '>=', startDate),
        where('boardDueDate', '<=', endDate)
      );
    } else if (reportType === 'cards') {
      q = query(
        collection(db, 'cards'),
        where('cardCreationDate', '>=', startDate),
        where('dueDate', '<=', endDate)
      );
    }
const querySnapshot = await getDocs(q);
let data;
if (reportType === 'cards') {
  data = querySnapshot.docs.map(doc => {
    const card = doc.data();
    return {
      ...card,
      id: doc.id // so generateExcel can use this as CardID
    };
  });
} else {
  data = querySnapshot.docs.map(doc => doc.data());
}
return data;


    return data;
  } catch (error) {
    throw error;
  }
};

// export const fetchReportData = async (reportType, startDate, endDate) => {
//   try {
//     let q;
    
//     if (reportType === 'projects') {
//       q = query(
//         collection(db, 'projects'),
//         where('startDate', '>=', startDate),
//         where('dueDate', '<=', endDate)
//       );
//     } else if (reportType === 'boards') {
//       q = query(
//         collection(db, 'boards'),
//         where('boardcreationDate', '>=', startDate),
//         where('boardDueDate', '<=', endDate)
//       );
//     } else if (reportType === 'cards') {
//       q = query(
//         collection(db, 'cards'),
//         where('cardCreationDate', '>=', startDate),
//         where('dueDate', '<=', endDate)
//       );
//     }

//     const querySnapshot = await getDocs(q);
//     const data = querySnapshot.docs.map(doc => doc.data());
//     return data;
//   } catch (error) {
   
//     throw error;
//   }
// };


export const updateUserGroups = async (userId, assignedGroups) => {
  try {
    const userDoc = doc(db, "users", userId);
    await updateDoc(userDoc, { assignedGroups });
    return assignedGroups; // Return updated groups for consistency
  } catch (error) {
    
    throw error;
  }
};

// //uncomment for sorting via database alphabeticall only: also uncomment UseEffect in AddcardDialog:fetchUsertenants
// export const fetchUsersAndTenants = async () => {
//   try {
//     // ✅ Sort users by userName in Firestore
//     const usersQuery = query(
//       collection(db, "users"),
//       orderBy("userName")
//     );
//     const usersCollection = await getDocs(usersQuery);
//     const usersData = usersCollection.docs.map(doc => ({ id: doc.id, ...doc.data() }));

//     // Fetch tenants as-is
//     const tenantsCollection = await getDocs(collection(db, "tenant"));
//     const tenantsData = tenantsCollection.docs.map(doc => ({ id: doc.id, ...doc.data() }));

//     return { users: usersData, tenants: tenantsData };
//   } catch (error) {
//     throw error;
//   }
// };

export const fetchUsersAndTenants = async () => {
  try {
    // Fetch users from Firestore
    const usersCollection = await getDocs(collection(db, "users"));
    const usersData = usersCollection.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Fetch tenants from Firestore
    const tenantsCollection = await getDocs(collection(db, "tenant"));
    const tenantsData = tenantsCollection.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    return { users: usersData, tenants: tenantsData };
  } catch (error) {
   
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    await deleteDoc(doc(db, "users", userId));
    return true; // Indicate success
  } catch (error) {
    
    throw error;
  }
};

export const addUser = async (newUser) => {
  try {
    const result = await addTenantUser(newUser.tenantId, newUser, { db });
    if (!result.success) {
      const error = new Error(result.reason || 'Unable to add user');
      error.reason = result.reason;
      error.activeUsers = result.activeUsers;
      error.userLimit = result.userLimit;
      throw error;
    }

    return result.userId;
  } catch (error) {
   
    throw error;
  }
};

// Fetch users for dropdowns filtered by tenantId
export const fetchTenantUsers = async (tenantId) => {
  try {
    const q = query(collection(db, 'users'), where('tenantId', '==', tenantId));
    const usersSnapshot = await getDocs(q);
    return usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw error;
  }
};

export const fetchUserIdByEmail = async (email, preferredTenantId = '') => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", email));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const allUsers = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const selectedUser =
        (preferredTenantId
          ? allUsers.find((u) => (u.tenantId || '').trim() === (preferredTenantId || '').trim())
          : null) || allUsers[0];

      // return querySnapshot.docs[0].data().userId; // Return userId
      return selectedUser;
    } else {
 
      return null;
    }
  } catch (error) {
    
    throw error;
  }
};

export const fetchCreatedByUserName = async (userId) => {
  try {
    if (!userId) return "Unknown User";

    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);

    return userDoc.exists() ? userDoc.data().userName : "Unknown User"; // Assuming 'userName' field exists
  } catch (error) {
   
    return "Unknown User";
  }
};

export const fetchLastSerialNumber = async (projectId) => {
  try {
    if (!projectId) throw new Error("Project ID is required");

    // First, get all cards for this project to find the highest number
    const cardsRef = collection(db, "cards");
    const q = query(cardsRef, where("projectId", "==", projectId));
    const querySnapshot = await getDocs(q);
    
    let highestNumber = 0;
    querySnapshot.forEach((doc) => {
      const cardData = doc.data();
      if (cardData.cardId) {
        // Extract the number from the card ID (e.g., "TES-8" -> 8)
        const match = cardData.cardId.match(/-(\d+)$/);
        if (match) {
          const number = parseInt(match[1], 10);
          highestNumber = Math.max(highestNumber, number);
        }
      }
    });

    // Store the highest number in the project document
    const projectRef = doc(db, "projects", projectId);
    await updateDoc(projectRef, { lastSerialNumber: highestNumber });
    
    return highestNumber;
  } catch (error) {
   
    throw error;
  }
};

export const updateLastSerialNumber = async (projectId, newSerialNumber) => {
  try {
    if (!projectId) throw new Error("Project ID is required");
    
    // First verify this is actually the highest number
    const currentHighest = await fetchLastSerialNumber(projectId);
    const numberToSet = Math.max(currentHighest, newSerialNumber);
    
    const projectRef = doc(db, "projects", projectId);
    await updateDoc(projectRef, { lastSerialNumber: numberToSet });
  } catch (error) {
   
    throw error;
  }
};

export const fetchUserNameById = async (userId) => {
  try {
    if (!userId) return "Unknown User"; // Handle empty userId

    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);

    return userDoc.exists() ? userDoc.data().userName || "Unknown User" : "Unknown User";
  } catch (error) {
    
    return "Unknown User";
  }
};

// Function to fetch user email using userId
export const fetchUserEmailById = async (userId) => {
  try {
    if (!userId) {
     
      return null;
    }

    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData?.email || null; // Return email if it exists
    } else {
     
      return null;
    }
  } catch (error) {
   
    return null;
  }
};

export const saveCard = async (task, selectedBoardId, selectedCard, tasks, tenantId) => {
  if (!selectedBoardId || selectedBoardId === 'ALL_BOARDS') {
    console.error('Invalid board selection:', selectedBoardId);
    throw new Error('Please select a valid board to save the card.');
  }

  try {
    console.log('Starting saveCard operation with:', {
      task,
      selectedBoardId,
      selectedCard: selectedCard?.id,
      tenantId
    });
    
    const boardDoc = doc(db, 'boards', selectedBoardId);
    const boardSnapshot = await getDoc(boardDoc);

    if (!boardSnapshot.exists()) {
      console.error('Board not found:', selectedBoardId);
      throw new Error('Selected board not found. Please refresh and try again.');
    }

    const boardData = boardSnapshot.data();
    const projectId = boardData.projectId;
    
    if (!projectId) {
      console.error('Project ID not found in board data');
      throw new Error('Project ID not found. Please refresh and try again.');
    }
   
    const cardData = {
      ...task, // <-assignedSprint will be included here
      boardId: selectedBoardId,
      projectId,
      tenantId,
      order: task.order ?? 999, // Add order field with default value for within Column sorting
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    console.log('Card data prepared:', cardData);
    
    if (selectedCard) {
      console.log('Updating existing card:', selectedCard.id);
      const cardDoc = doc(db, 'cards', selectedCard.id);
      await updateDoc(cardDoc, cardData);
      console.log('Card updated successfully');
      return tasks.map((c) => 
        c.id === selectedCard.id ? { ...cardData, id: selectedCard.id } : c
      );
    } else {
      console.log('Creating new card');
      const docRef = doc(collection(db, 'cards'));
      await deductTenantCredits(tenantId, 'addCard', 1, {
        userId: task.cardCreatedByUserEmail || task.createdByUserEmail || '',
        referenceId: docRef.id,
      });
      await setDoc(docRef, cardData);
      console.log('New card created with ID:', docRef.id);
      return [...tasks, { ...cardData, id: docRef.id }];
    }
  } catch (error) {
    console.error('Error saving card:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    throw error;
  }
};

// function to set seed data for any new setup of the app. this routine will add the collection and single placeholder data
export const setSeedData = async (tenantId) => {
  // logic to be added
}

export const updateAllTemplatesWithWithdrawnColumn = async () => {
  try {
    const templatesCollection = collection(db, "templates");
    const templateSnapshot = await getDocs(templatesCollection);
    
    const batch = writeBatch(db);
    
    templateSnapshot.docs.forEach(doc => {
      const templateData = doc.data();
      const columns = templateData.columns || [];
      
      // Only add 'Withdrawn' if it doesn't already exist
      if (!columns.includes('Withdrawn')) {
        const updatedColumns = [...columns, 'Withdrawn'];
        batch.update(doc.ref, { columns: updatedColumns });
      }
    });
    
    await batch.commit();
    
  } catch (error) {
  
    throw error;
  }
};

export const updateAllBoardsWithWithdrawnColumn = async (tenantId) => {
  try {
    const boardsCollection = collection(db, "boards");
    // Fetch boards for the specific tenant
    const q = query(boardsCollection, where("tenantId", "==", tenantId));
    const boardSnapshot = await getDocs(q);
    
    const batch = writeBatch(db);
    
    boardSnapshot.docs.forEach(doc => {
      const boardData = doc.data();
      const columns = boardData.columns || [];
      
      // Only add 'Withdrawn' if it doesn't already exist
      if (!columns.includes('Withdrawn')) {
        const updatedColumns = [...columns, 'Withdrawn'];
        batch.update(doc.ref, { columns: updatedColumns });
      }
    });
    
    await batch.commit();
    console.log('Successfully updated all boards with Withdrawn column');
  } catch (error) {
    console.error('Error updating boards:', error);
    throw error;
  }
};

// export const fetchRiskCards = async (projectId, boardId = null, tenantId) => {
//   try {
//     let q = query(
//       collection(db, "cards"),
//       where("projectId", "==", projectId),
//       where("tenantId", "==", tenantId),
//       where("cardType", "==", "Risk")
//     );

//     if (boardId) {
//       q = query(
//         collection(db, "cards"),
//         where("projectId", "==", projectId),
//         where("tenantId", "==", tenantId),
//         where("boardId", "==", boardId),
//         where("cardType", "==", "Risk")
//       );
//     }

//     const querySnapshot = await getDocs(q);
//     return querySnapshot.docs.map((doc) => ({
//       id: doc.id,
//       ...doc.data(),
//     }));
//   } catch (error) {
//     console.error("Error fetching Risk cards:", error);
//     return [];
//   }
// };

export const subscribeToRiskCards = (projectId, tenantId, boardId = null, callback) => {
  try {
    let q = query(
      collection(db, "cards"),
      where("projectId", "==", projectId),
      where("tenantId", "==", tenantId),
      where("cardType", "==", "Risk")
    );

    if (boardId) {
      q = query(
        collection(db, "cards"),
        where("projectId", "==", projectId),
        where("tenantId", "==", tenantId),
        where("boardId", "==", boardId),
        where("cardType", "==", "Risk")
      );
    }

    // Real-time listener
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const cards = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(cards);
    });

    return unsubscribe; // allow cleanup
  } catch (error) {
    console.error("Error subscribing to Risk cards:", error);
    return () => {};
  }
};

export const subscribeToIssueCards = (projectId, tenantId, boardId = null, callback) => {
  try {
    let q = query(
      collection(db, "cards"),
      where("projectId", "==", projectId),
      where("tenantId", "==", tenantId),
      where("cardType", "==", "Issue")
    );

    if (boardId) {
      q = query(
        collection(db, "cards"),
        where("projectId", "==", projectId),
        where("tenantId", "==", tenantId),
        where("boardId", "==", boardId),
        where("cardType", "==", "Issue")
      );
    }

    // Real-time listener
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const cards = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(cards);
    });

    return unsubscribe; // allow cleanup
  } catch (error) {
    console.error("Error subscribing to Issue cards:", error);
    return () => {};
  }
};

// Admin Management Functions
export const addAdminToTenant = async (tenantId, adminEmail, adminName) => {
  try {
    const adminRef = await addDoc(collection(db, "tenantAdmins"), {
      tenantId,
      adminEmail: adminEmail.toLowerCase(),
      adminName,
      createdAt: new Date().toISOString()
    });
    return { id: adminRef.id, tenantId, adminEmail: adminEmail.toLowerCase(), adminName };
  } catch (error) {
    console.error("Error adding admin to tenant:", error);
    throw error;
  }
};

export const fetchTenantAdmins = async (tenantId) => {
  try {
    const q = query(collection(db, "tenantAdmins"), where("tenantId", "==", tenantId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching tenant admins:", error);
    throw error;
  }
};

export const removeAdminFromTenant = async (adminId) => {
  try {
    await deleteDoc(doc(db, "tenantAdmins", adminId));
    return true;
  } catch (error) {
    console.error("Error removing admin from tenant:", error);
    throw error;
  }
};
